import { Injectable } from '@angular/core';

import { Actions, Effect, ofType } from '@ngrx/effects';
import { switchMap, map, catchError, tap, take, exhaustMap } from 'rxjs/operators';
import { forkJoin, Observable, of } from 'rxjs';
import { Store } from '@ngrx/store';

import { GlossariService } from '../glossari.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import * as glossariAction from './glossari.actions';
import { AuthService } from 'src/app/auth/auth.service';
import { AppState, getMyGlossari } from 'src/app/store/app.state';
import { Glossary } from 'src/app/shared/models/glossary.model';
import { TraduzioniService } from '../../traduzioni/traduzioni.service';
import { Translation } from 'src/app/shared/models/translation.model';
import { Attachment } from 'src/app/shared/models/attachment.model';
import { FilterData } from 'src/app/shared/models/filter-data/filter-data.model';

/**Classe che gestisce i side effect per la sezione glossari dello store */
@Injectable()
export class GlossariEffects {
    /**
     * Costruttore per GlossariEffects
     * @param actions$ {Actions}
     * @param glossariService {GlossariService} servizi che gestiscono chiamate a BE per la sezione glossari
     * @param utility {UtilityService} servizio che gestisce una serie di funzionalità di uso comune
     * @param authService {AuthService} servizi disponibili per la sezione auth
     * @param store {Store<AppState>} store a livello dell'applicazione
     * @param traduzioniService {TraduzioniService} classe che gestisce i servizi per la sezione Traduzioni
     */
    constructor(
        private actions$: Actions,
        private glossariService: GlossariService,
        private utility: UtilityService,
        private authService: AuthService,
        private store: Store<AppState>,
        private traduzioniService: TraduzioniService
    ) { }

    /**Side effect per l'azione di inizio recupero elenco glossari */
    @Effect()
    startGetGlossari = this.actions$.pipe(
        ofType(glossariAction.START_GET_LIST_GLOS),
        exhaustMap((action: glossariAction.StartGetListGlos) => {
            this.store.dispatch(new glossariAction.IsLoadingTabella({ sezione: action.payload, valore: true }));
            let bodyPost = {};
            this.store.select(getMyGlossari).pipe(take(1)).subscribe(data => {
                if (action.payload === glossariAction.tabellaGlossari.main || action.payload === glossariAction.tabellaGlossari.mainFilterSort) {
                    bodyPost = <FilterData>{
                        filter: data.elencoFiltriVoci ? data.elencoFiltriVoci : null,
                        fromRow: data.fromRow !== undefined ? data.fromRow : 0,
                        toRow: data.toRow !== undefined ? data.toRow : 25,
                        orderBy: data.ordinamentoVoci ? data.ordinamentoVoci : [],
                        reload: action.reload
                    }
                } else {
                    if (data.elencoFiltriVociModale) {
                        bodyPost = <FilterData>{
                            ...bodyPost,
                            filter: data.elencoFiltriVociModale,
                            reload: action.reload
                        };
                    }
                    if (data.fromRowModal !== undefined && data.toRowModal !== undefined) {
                        bodyPost = <FilterData>{
                            ...bodyPost,
                            reload: action.reload,
                            fromRow: data.fromRowModal,
                            toRow: data.toRowModal
                        }
                    }
                }
            })
            return this.glossariService.getListaGlossari(bodyPost).pipe(
                map(resData => {
                    return new glossariAction.GetListGlosSuccess({ sezione: action.payload, elenco: resData });
                }),
                catchError(errRes => {
                    this.store.dispatch(new glossariAction.IsLoadingTabella({ sezione: action.payload, valore: true }));
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('getListaGlossari.' + errRes.error));
                    }
                    return of(new glossariAction.GetListGlosFailure(errRes));
                })
            )
        })
    );

    /**Side effect per l'avvenuto recupero dell'elenco glossari */
    @Effect({ dispatch: false })
    getListGlosSuccess = this.actions$.pipe(
        ofType(glossariAction.GET_LIST_GLOS_SUCCESS),
        tap((action: glossariAction.GetListGlosSuccess) => {
            this.store.dispatch(new glossariAction.IsLoadingTabella({ sezione: action.payload.sezione, valore: false }));
            this.authService.refreshLogoutTimer();
        })
    );

    /**Side effect per l'azione di inizio modifica di una voce glossario */
    @Effect()
    startUpdateGlossary = this.actions$.pipe(
        ofType(glossariAction.START_UPDATE_GLOSSARY),
        switchMap((action: glossariAction.StartUpdateGlossary) =>
            this.glossariService.updateGlossary(action.payload.idGlos, action.payload.nomeCampo, action.payload.newValue).pipe(
                map(resData => new glossariAction.UpdateGlossarySuccess({ nuovoGlos: resData, campoMod: action.payload.nomeCampo }, action.isUndoRedoOperation)),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('updateGlossary.' + errRes.error));
                    }
                    return of(new glossariAction.UpdateGlossaryFailure(errRes));
                })
            )
        )
    );

    /**Side effect per l'avvenuta modifica della voce di glossario */
    @Effect({ dispatch: false })
    updateGlossarySuccess = this.actions$.pipe(
        ofType(glossariAction.UPDATE_GLOSSARY_SUCCESS),
        tap(() => {
            this.utility.gestioneSnackbar(this.utility.getTranslatedString('updateGlossary.success'));
            this.authService.refreshLogoutTimer();
        })
    );

    /**Side effect per l'inizio di modifica di campi multipli di una voce di glossario */
    @Effect()
    startUpdateMultipleFields = this.actions$.pipe(
        ofType(glossariAction.START_UPDATE_MULTIPLE_FIELDS),
        switchMap((action: glossariAction.StartUpdateMultipleFields) =>
            this.glossariService.postGlossaryUpdate(action.payload.idGlos, action.payload.modifiedProp).pipe(
                map(resData => new glossariAction.UpdateMultipleFieldsSuccess(resData)),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('updateGlossary.' + errRes.error));
                    }
                    return of(new glossariAction.UpdateMultipleFieldsFailure());
                })
            )
        )
    );

    /**Side effect per l'avvenuta modifica di campi multipli di una voce di glossario */
    @Effect({ dispatch: false })
    updateMultipleFieldsSuccess = this.actions$.pipe(
        ofType(glossariAction.UPDATE_MULTIPLE_FIELDS_SUCCESS),
        tap(() => {
            this.utility.gestioneSnackbar(this.utility.getTranslatedString('updateGlossary.success'));
            this.authService.refreshLogoutTimer();
        })
    );

    /**Side effect per la selezione di una voce di glossario */
    @Effect()
    selectGlossary = this.actions$.pipe(
        ofType(glossariAction.SELECT_GLOSSARY),
        map((action: glossariAction.SelectGlossary) => action),
        switchMap((action) => {
            let actionList: any[] = [
                new glossariAction.StartGetTranslations(action.payload.id),
                new glossariAction.StartGetSeeAlso(action.payload.id),
                new glossariAction.StartGetSeeAlsoIncome(action.payload.id)
            ];
            if (action.payload.hasLongDescription) {
                actionList.push(new glossariAction.StartGetGlossaryAttachment(action.payload.id));
            }
            return actionList;
        })
    );

    /**Side effect per l'azione di inizio recupero dei riferimenti alla voce di glossario selezionata */
    @Effect()
    startGetTranslations = this.actions$.pipe(
        ofType(glossariAction.START_GET_TRANSLATIONS),
        switchMap((action: glossariAction.StartGetTranslations) =>
            this.glossariService.getTranslations(action.payload).pipe(
                map(resData => {
                    for (let reference of resData) {
                        reference.idTaggedGlossRef = action.payload;
                    }
                    return new glossariAction.GetTranslationsSuccess(resData);
                }),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('getTranslationsForGlossary.' + errRes.error));
                    }
                    return of(new glossariAction.GetTranslationsFailure(errRes));
                })
            )
        )
    );

    /**Side effect per l'azione di avvenuto recupero dei riferimenti alla voce di glossario selezionara */
    @Effect({ dispatch: false })
    getTranslationsSuccess = this.actions$.pipe(
        ofType(glossariAction.GET_TRANSLATIONS_SUCCESS),
        tap(() => this.authService.refreshLogoutTimer())
    );

    /**Side effect per l'azione di inizio recupero dei see also alla voce di glossario selezionata */
    @Effect()
    startGetSeeAlso = this.actions$.pipe(
        ofType(glossariAction.START_GET_SEEALSO),
        switchMap((action: glossariAction.StartGetSeeAlso) =>
            this.glossariService.getSeeAlso(action.payload).pipe(
                map(resData => new glossariAction.GetSeeAlsoSuccess(resData)),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('getSeeAlsoForGlossary.' + errRes.error));
                    }
                    return of(new glossariAction.GetSeeAlsoFailure(errRes));
                })
            )
        )
    );

    /**Side effect per l'azione di avvenuto recupero dei see also alla voce di glossario selezionata */
    @Effect({ dispatch: false })
    getSeeAlsoSuccess = this.actions$.pipe(
        ofType(glossariAction.GET_SEEALSO_SUCCESS),
        tap(() => this.authService.refreshLogoutTimer())
    );

    /**Side effect per l'azione di inizio inserimento di un nuovo collegamento a glossario come vedi anche */
    @Effect()
    startAddSeeAlso = this.actions$.pipe(
        ofType(glossariAction.START_ADD_SEEALSO),
        switchMap((action: glossariAction.StartAddSeeAlso) =>
            this.glossariService.addNewSeeAlso(action.payload.idGlos, action.payload.seeAlso.id).pipe(
                map(() => new glossariAction.AddSeeAlsoSuccess(action.payload.seeAlso)),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('addSeeAlsoForGlossary.' + errRes.error));
                    }
                    return of(new glossariAction.AddSeeAlsoFailure());
                })
            )
        )
    );

    /**Side effect per l'azione di avvenuto inserimento di un nuovo collegamento a glossario come vedi anche */
    @Effect({ dispatch: false })
    addSeeAlsoSuccess = this.actions$.pipe(
        ofType(glossariAction.ADD_SEEALSO_SUCCESS),
        tap(() => {
            this.utility.gestioneSnackbar(this.utility.getTranslatedString('addSeeAlsoForGlossary.success'));
            this.authService.refreshLogoutTimer();
        })
    );

    /**Side effect per l'azione di inizio rimozione di un collegamento a glossario come vedi anche */
    @Effect()
    startRemoveSeeAlso = this.actions$.pipe(
        ofType(glossariAction.START_REMOVE_SEEALSO),
        switchMap((action: glossariAction.StartRemoveSeeAlso) =>
            this.glossariService.removeSeeAlso(action.payload.idGlos, action.payload.idSeeAlso).pipe(
                map(() => new glossariAction.RemoveSeeAlsoSuccess(action.payload.idSeeAlso)),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('removeSeeAlsoForGlossary.' + errRes.error));
                    }
                    return of(new glossariAction.RemoveSeeAlsoFailure());
                })
            )
        )
    );

    /**Side effect per l'azione di avvenuta rimozione di un collegamento a glossario come vedi anche */
    @Effect({ dispatch: false })
    removeSeeAlsoSuccess = this.actions$.pipe(
        ofType(glossariAction.REMOVE_SEEALSO_SUCCESS),
        tap(() => {
            this.utility.gestioneSnackbar(this.utility.getTranslatedString('removeSeeAlsoForGlossary.success'));
            this.authService.refreshLogoutTimer();
        })
    );

    /**Side effect per l'azione di inizio aggiunta di una nuova voce di glossario */
    @Effect()
    startAddNewGlossary = this.actions$.pipe(
        ofType(glossariAction.START_ADD_NEW_GLOSSARY),
        switchMap((action: glossariAction.StartAddNewGlossary) =>
            this.glossariService.addNewGlossary(action.payload.newGlos).pipe(
                map((resData: Glossary) => new glossariAction.AddNewGlossarySuccess({ newGlos: resData, filtri: action.payload.filtri }, action.isUndoRedoOperation)),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('addNewGlossary.' + errRes.error));
                    }
                    return of(new glossariAction.AddNewGlossaryFailure());
                })
            )
        )
    );

    /**Side effect per l'azione di avvenuta aggiunta di una nuova voce di glossario */
    @Effect()
    addNewGlossarySuccess = this.actions$.pipe(
        ofType(glossariAction.ADD_NEW_GLOSSARY_SUCCESS),
        map(() => new glossariAction.StartGetListGlos(glossariAction.tabellaGlossari.mainFilterSort))
    );

    /**Side effect per l'azione di inizio rimozione di una voce di glossario */
    @Effect()
    startRemoveGlossary = this.actions$.pipe(
        ofType(glossariAction.START_REMOVE_GLOSSARY),
        switchMap((action: glossariAction.StartRemoveGlossary) =>
            this.glossariService.removeGlossary(action.payload).pipe(
                map(() => new glossariAction.RemoveGlossarySuccess(action.payload, action.isUndoRedoOperation)),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('removeGlossary.' + errRes.error));
                    }
                    return of(new glossariAction.RemoveGlossaryFailure());
                })
            )
        )
    );

    /**Side effect per l'azione di avvenuta rimozione di una voce di glossario */
    @Effect({ dispatch: false })
    removeGlossarySuccess = this.actions$.pipe(
        ofType(glossariAction.REMOVE_GLOSSARY_SUCCESS),
        tap(() => {
            this.utility.gestioneSnackbar(this.utility.getTranslatedString('removeGlossary.success'));
            this.authService.refreshLogoutTimer();
        })
    );

    /**Side effect per l'azione di inizio ripristino di una voce di glossario precedentemente rimossa */
    @Effect()
    startRestoreGlos = this.actions$.pipe(
        ofType(glossariAction.START_RESTORE_GLOS),
        switchMap((action: glossariAction.StartRestoreGlos) =>
            this.glossariService.restoreGlossary(action.payload).pipe(
                switchMap(resData => {
                    return [
                        new glossariAction.RestoreGlosSuccess,
                        new glossariAction.StartGetListGlos(glossariAction.tabellaGlossari.mainFilterSort)
                    ]
                }),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('restoreGlos.' + errRes.error));
                    }
                    return of(new glossariAction.RestoreGlosFailure());
                })
            )
        )
    );

    /**Side effect per l'avvenuta rimozione di un utente */
    @Effect({ dispatch: false })
    restoreUserSuccess = this.actions$.pipe(
        ofType(glossariAction.RESTORE_GLOS_SUCCESS),
        tap(() => {
            this.utility.gestioneSnackbar(this.utility.getTranslatedString("restoreGlos.success"));
            this.authService.refreshLogoutTimer();
        })
    );

    /**Side effect per l'azione di inizio aggiornamento della traduzione di un riferimento/traduzione */
    @Effect()
    startUpdateReferenceTransl = this.actions$.pipe(
        ofType(glossariAction.START_UPDATE_REFERENCE_TRANSL),
        switchMap((action: glossariAction.StartUpdateReferenceTransl) =>
            this.traduzioniService.updateTranslation(action.payload.idRef, action.payload.newTranslation).pipe(
                map((resData: Translation) => new glossariAction.UpdateReferenceTranslSuccess(resData)),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('updateTranslation.' + errRes.error));
                    }
                    return of(new glossariAction.UpdateReferenceTranslFailure());
                })
            )
        )
    );

    /**Side effect per l'azione di avvenuto aggiornamento della traduzione di un riferimento/traduzione */
    @Effect({ dispatch: false })
    updateReferenceTranslSuccess = this.actions$.pipe(
        ofType(glossariAction.UPDATE_REFERENCE_TRANSL_SUCCESS),
        tap(() => {
            this.utility.gestioneSnackbar(this.utility.getTranslatedString('updateTranslation.success'));
            this.authService.refreshLogoutTimer();
        })
    );

    /**Side effect per l'azione di inizio rimozione di un tag glossario da una stringa di traduzione (disassocia glossario da stringa) in sezione Glossari */
    @Effect()
    startDissociateGlosFromGlosSect = this.actions$.pipe(
        ofType(glossariAction.START_DISSOCIATE_GLOS_FROM_GLOS_SECT),
        switchMap((action: glossariAction.StartDissociateGlosFromGlosSect) =>
            this.traduzioniService.getDissociateGlossary(action.payload.translationId, action.payload.glossaryId).pipe(
                map(resData => {
                    return new glossariAction.DissociateGlosFromGlosSectSuccess({ glossaryId: action.payload.glossaryId, referenceNumber: action.payload.referenceNumber, translationId: action.payload.translationId }, action.isIncomeSeeAlsoTransl);
                }),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('getDissociateGlossary.' + errRes.error));
                    }
                    return of(new glossariAction.DissociateGlosFromGlosSectFailure());
                })
            )
        )
    );

    /**Side effect per l'azione di avvenuta rimozione di un tag glossario da una stringa di traduzione (disassocia glossario da stringa) in sezione Glossari */
    @Effect()
    dissociateGlosFromGlosSectSuccess = this.actions$.pipe(
        ofType(glossariAction.DISSOCIATE_GLOS_FROM_GLOS_SECT_SUCCESS),
        map((action: glossariAction.DissociateGlosFromGlosSectSuccess) => {
            this.utility.gestioneSnackbar(this.utility.getTranslatedString('getDissociateGlossary.success'));
            this.authService.refreshLogoutTimer();
            if (action.payload.referenceNumber > 1) { //Se ci sono riferimenti multipli alla voce questa rimane
                return action.isIncomeSeeAlsoTransl ? new glossariAction.DisplayGlosHistory(false) : new glossariAction.StartGetTranslations(action.payload.glossaryId);
            } else { //In presenza di un unico riferimento la voce viene rimossa
                return new glossariAction.StartGetListGlos(glossariAction.tabellaGlossari.mainFilterSort, true); //forzo il reload dei dati per recepire la cancellazione della voce di glossario
            }
        })
    );

    /**Side effect per l'azione di inizio rimozione di un glossario da tutti i suoi riferimenti/traduzioni (disassocia glossario da tutte le stringhe) */
    @Effect()
    startDissociateAllReferences = this.actions$.pipe(
        ofType(glossariAction.START_DISSOCIATE_ALL_REFERENCES),
        switchMap((action: glossariAction.StartDissociateAllReferences) =>
            this.glossariService.getDissociateAllReferences(action.payload).pipe(
                map(resData => {
                    return new glossariAction.DissociateAllReferencesSuccess({ dissociationMex: resData, glossaryId: action.payload });
                }),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('getDissociateAllReferencesFromGlossary.' + errRes.error));
                    }
                    return of(new glossariAction.DissociateAllReferencesFailure());
                })
            )
        )
    );

    /**Side effect per l'azione di avvenuta rimozione di un glossario da tutti i suoi riferimenti/traduzioni (disassocia glossario da tutte le stringhe */
    @Effect()
    dissociateAllReferencesSuccess = this.actions$.pipe(
        ofType(glossariAction.DISSOCIATE_ALL_REFERENCES_SUCCESS),
        map((action: glossariAction.DissociateAllReferencesSuccess) => {
            let successMexList = action.payload.dissociationMex.split(' ');
            let canceled = successMexList[2];
            let total = successMexList[4];
            this.utility.gestioneSnackbar(action.payload.dissociationMex);
            if (total === canceled) {
                return new glossariAction.StartGetListGlos(glossariAction.tabellaGlossari.mainFilterSort, true);
            } else {
                return new glossariAction.StartGetTranslations(action.payload.glossaryId);
            }
        })
    );

    /**Side effect per l'azione di inizio recupero dell'elenco note legate a una stringa riferimento/traduzione */
    @Effect()
    startGetReferenceNotes = this.actions$.pipe(
        ofType(glossariAction.START_GET_REFERENCE_NOTES),
        switchMap((action: glossariAction.StartGetReferenceNotes) =>
            this.traduzioniService.getNotes(action.payload).pipe(
                map(resData => new glossariAction.GetReferenceNotesSuccess(resData)),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('getTranslationNotes.' + errRes.error));
                    }
                    return of(new glossariAction.GetReferenceNotesFailure());
                })
            )
        )
    );

    /**Side effect per l'azione di avvenuto recupero dell'elenco note legate a una stringa riferimento/traduzione */
    @Effect({ dispatch: false })
    getReferenceNotesSuccess = this.actions$.pipe(
        ofType(glossariAction.GET_REFERENCE_NOTES_SUCCESS),
        tap(() => {
            this.authService.refreshLogoutTimer();
        })
    );

    /**Side effect per l'azione di inizio recupero dell'elenco di glossari taggati in una stringa riferimento/traduzione */
    @Effect()
    startGetReferenceGlossaries = this.actions$.pipe(
        ofType(glossariAction.START_GET_REFERENCE_GLOSSARIES),
        switchMap((action: glossariAction.StartGetReferenceGlossaries) =>
            this.traduzioniService.getGlossaries(action.payload).pipe(
                map(resData => new glossariAction.GetReferenceGlossariesSuccess(resData)),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('getTranslationGlossaries.' + errRes.error));
                    }
                    return of(new glossariAction.GetReferenceGlossariesFailure());
                })
            )
        )
    );

    /**Side effect per l'azione di avvenuto recupero dell'elenco di glossari taggati in una stringa riferimento/traduzione */
    @Effect({ dispatch: false })
    getReferenceGlossariesSuccess = this.actions$.pipe(
        ofType(glossariAction.GET_REFERENCE_GLOSSARIES_SUCCESS),
        tap(() => {
            this.authService.refreshLogoutTimer();
        })
    );

    /**Side effect per l'azione di inizio recupero dello storico di una voce di glossario */
    @Effect()
    startGetGlosHistory = this.actions$.pipe(
        ofType(glossariAction.START_GET_GLOS_HISTORY),
        switchMap((action: glossariAction.StartGetGlosHistory) =>
            this.glossariService.getGlossaryHistory(action.payload).pipe(
                map(resData => new glossariAction.GetGlosHistorySuccess(resData)),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('getGlossaryHistory.' + errRes.error));
                    }
                    return of(new glossariAction.GetGlosHistoryFailure());
                })
            )
        )
    );

    /**Side effect per l'azione di avvenuto recupero dello storico di una voce di glossario */
    @Effect({ dispatch: false })
    getGlosHistorySuccess = this.actions$.pipe(
        ofType(glossariAction.GET_GLOS_HISTORY_SUCCESS),
        tap(() => {
            this.authService.refreshLogoutTimer();
        })
    );

    /**Side effect per l'azione di inizio associazione dei riferimenti di più glossari su un unico glossario target (usato per riaccorpare doppioni di una stessa voce) */
    @Effect()
    startAssociateMultipleGlos = this.actions$.pipe(
        ofType(glossariAction.START_ASSOCIATE_MULTIPLE_GLOS),
        switchMap((action: glossariAction.StartAssociateMultipleGlos) =>
            this.glossariService.postAssociateMultipleReferences(action.payload.target, action.payload.source).pipe(
                map((resData: string) => new glossariAction.AssociateMultipleGlosSuccess({ glosId: action.payload.target, responseMex: resData })),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('postAssociateGlossToMultipleReferences.' + errRes.error));
                    }
                    return of(new glossariAction.AssociateMultipleGlosFailure());
                })
            )
        )
    );

    /**Side effect per l'azione di avvenuta associazione dei riferimenti di più glossari su un unico glossario target (usato per riaccorpare doppioni di una stessa voce) */
    @Effect()
    associateMultipleGlosSuccess = this.actions$.pipe(
        ofType(glossariAction.ASSOCIATE_MULTIPLE_GLOS_SUCCESS),
        tap((action: glossariAction.AssociateMultipleGlosSuccess) => {
            this.utility.gestioneSnackbar('Associa glossari: ' + action.payload.responseMex);
            this.authService.refreshLogoutTimer();
        }),
        map((action: glossariAction.AssociateMultipleGlosSuccess) => new glossariAction.StartGetListGlos(glossariAction.tabellaGlossari.mainFilterSort, true))
    );

    /**Side effect per l'azione di inizio recupero delle traduzioni in cui è taggato un glossario che punta al selezionato come vedi anche */
    @Effect()
    startGetSeeAlsoIncomeTranslations = this.actions$.pipe(
        ofType(glossariAction.START_GET_SEEALSO_INCOME_TRANSLATIONS),
        switchMap((action: glossariAction.StartGetSeeAlsoIncomeTranslations) =>
            this.glossariService.getSeeAlsoIncomeTranslations(action.payload).pipe(
                map(resData => new glossariAction.GetSeeAlsoIncomeTranslationsSuccess(resData)),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('getSeeAlsoIncomeTranslations.' + errRes.error));
                    }
                    return of(new glossariAction.GetSeeAlsoIncomeTranslationsFailure());
                })
            )
        )
    );

    /**Side effect per l'azione di avvenuto recupero delle traduzioni in cui è taggato un glossario che punta al selezionato come vedi anche */
    @Effect({ dispatch: false })
    getSeeAlsoIncomeTranslationsSuccess = this.actions$.pipe(
        ofType(glossariAction.GET_SEEALSO_INCOME_TRANSLATIONS_SUCCESS),
        tap(() => {
            this.authService.refreshLogoutTimer();
        })
    );

    /**Side effect per l'azione di inizio recupero dei glossari che puntano al selezionato come vedi anche */
    @Effect()
    startGetSeeAlsoIncome = this.actions$.pipe(
        ofType(glossariAction.START_GET_SEEALSO_INCOME),
        switchMap((action: glossariAction.StartGetSeeAlsoIncome) =>
            this.glossariService.getSeeAlsoIncome(action.payload).pipe(
                map(resData => {
                    let getTranslationsList: Observable<Translation[]>[] = [];
                    let indexArray: { id: number, name: string }[] = [];
                    for (let res of resData) {
                        indexArray.push({ id: res.id, name: res.name });
                        getTranslationsList.push(this.glossariService.getTranslations(res.id));
                    }
                    return new glossariAction.GetSeeAlsoIncomeSuccess({ indexArray: indexArray, observableList: getTranslationsList });
                }),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('getSeeAlsoIncome.' + errRes.error));
                    }
                    return of(new glossariAction.GetSeeAlsoIncomeFailure());
                })
            )
        )
    );

    /**Side effect per l'azione di avvenuto recupero dei glossari che puntano al selezionato come vedi anche */
    @Effect()
    getSeeAlsoIncomeSuccess = this.actions$.pipe(
        ofType(glossariAction.GET_SEEALSO_INCOME_SUCCESS),
        switchMap((action: glossariAction.GetSeeAlsoIncomeSuccess) =>
            forkJoin(action.payload.observableList).pipe(
                map(resData => {
                    let join: Translation[] = [];
                    for (let i = 0; i < resData.length; i++) {
                        let tempGlosOrigin = [...resData[i]];
                        tempGlosOrigin = tempGlosOrigin.map(trad => {
                            return {
                                ...trad,
                                idTaggedGlossRef: action.payload.indexArray[i].id,
                                nameTaggedGlosRef: action.payload.indexArray[i].name
                            };
                        })
                        join.push(...tempGlosOrigin);
                    }
                    return new glossariAction.GetSeeAlsoIncomeTranslationsSuccess(join);
                })
            )
        )
    );

    /**Side effect per l'azione di inizio recupero dell'allegato a un glossario */
    @Effect()
    startGetGlossaryAttachment = this.actions$.pipe(
        ofType(glossariAction.START_GET_GLOSSARY_ATTACHMENT),
        switchMap((action: glossariAction.StartGetGlossaryAttachment) =>
            this.glossariService.getGlossaryAttachment(action.payload).pipe(
                map((resData: Attachment) => new glossariAction.GetGlossaryAttachmentSuccess(resData)),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('getGlossaryAttachment.' + errRes.error));
                    }
                    return of(new glossariAction.GetGlossaryAttachmentFailure());
                })
            )
        )
    );

    /**Side effect per l'azione di avvenuto recupero dell'allegato a un glossario */
    @Effect({ dispatch: false })
    getGlossaryAttachmentSuccess = this.actions$.pipe(
        ofType(glossariAction.GET_SEEALSO_INCOME_TRANSLATIONS_SUCCESS),
        tap(() => {
            this.authService.refreshLogoutTimer();
        })
    );
}